import React, { useState, useEffect } from "react";
import { supabase } from "../config/supabase";
import { User } from "@supabase/supabase-js";
import { addDataRefreshListener } from "../utils/events";
import { Child } from "../types/child";
import { getGoalForDate, getLocalDateString, getWeeklyGoalTotal } from "../utils/goalUtils";
import CircularProgress from "../components/CircularProgress";

interface ProgressDashboardProps {
  user: User | null;
  darkMode: boolean;
  treatmentType?: "self" | "child";
  onNavigate?: (page: string) => void;
}

interface ChildProgress {
  child_id: string;
  child_name: string;
  today_minutes: number;
  today_goal: number;
  weekly_total: number;
  weekly_goal: number;
  goal_achievement_rate: number;
  consistency_rate: number;
  total_sessions: number;
  average_session_length: number;
  longest_streak: number;
  current_streak: number;
}

const ProgressDashboard: React.FC<ProgressDashboardProps> = ({
  user,
  darkMode,
  treatmentType = "self",
  onNavigate,
}) => {
  const [children, setChildren] = useState<Child[]>([]);
  const [childProgress, setChildProgress] = useState<ChildProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalTodayMinutes, setTotalTodayMinutes] = useState(0);
  const [totalWeeklyMinutes, setTotalWeeklyMinutes] = useState(0);

  useEffect(() => {
    if (user) {
      if (treatmentType === "child") {
        fetchChildren();
      } else {
        // For self-treatment users, fetch their own progress
        fetchSelfProgress();
      }
    } else {
      setChildren([]);
      setChildProgress([]);
      setLoading(false);
    }
  }, [user, treatmentType]);

  // Refresh on global data updates (settings/logs)
  useEffect(() => {
    const remove = addDataRefreshListener(() => {
      if (!user) return;
      if (treatmentType === "child") {
        fetchChildren();
      } else {
        fetchSelfProgress();
      }
    });
    return remove;
  }, [user, treatmentType]);

  const fetchChildren = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from("children")
        .select("*")
        .eq("user_id", user.id)
        .order("name");

      if (error) throw error;

      setChildren(data || []);

      if (data && data.length > 0) {
        fetchChildProgress(data);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching children:", error);
      setLoading(false);
    }
  };

  const fetchChildProgress = async (childrenList: Child[]) => {
    if (!user) return;

    try {
      const progressArray: ChildProgress[] = [];
      let totalToday = 0;
      let totalWeekly = 0;

      for (const child of childrenList) {
        // Get child goals
        const { data: goalsData } = await supabase
          .from("child_goals")
          .select(
            "weekly_goal_minutes, monday_goal_minutes, tuesday_goal_minutes, wednesday_goal_minutes, thursday_goal_minutes, friday_goal_minutes, saturday_goal_minutes, sunday_goal_minutes",
          )
          .eq("child_id", child.id)
          .single();

        const todayStr = getLocalDateString();
        const todayGoal = goalsData
          ? getGoalForDate(goalsData as any, todayStr)
          : null;
        // Rest day => 0 minutes goal (UI will show 100% in ring logic)
        const dailyGoal = todayGoal === null ? 0 : (todayGoal ?? 0);
        const weeklyGoal = goalsData
          ? getWeeklyGoalTotal(goalsData as any)
          : 0;

        // Get today's logs
        const today = todayStr;
        const { data: todayLogs, error: todayError } = await supabase
          .from("child_logs")
          .select("time_spent_minutes")
          .eq("child_id", child.id)
          .eq("date", today);

        if (todayError) continue;

        const todayMinutes =
          todayLogs?.reduce((sum, log) => sum + log.time_spent_minutes, 0) || 0;
        totalToday += todayMinutes;

        // Get this week's logs
        const weekStart = new Date();
        weekStart.setDate(weekStart.getDate() - weekStart.getDay());
        const weekStartStr = getLocalDateString(weekStart);
        const weekEndStr = todayStr;

        const { data: weekLogs, error: weekError } = await supabase
          .from("child_logs")
          .select("time_spent_minutes")
          .eq("child_id", child.id)
          .gte("date", weekStartStr)
          .lte("date", weekEndStr);

        if (weekError) continue;

        const weeklyTotal =
          weekLogs?.reduce((sum, log) => sum + log.time_spent_minutes, 0) || 0;
        totalWeekly += weeklyTotal;

        // Get overall stats for all time (for consistency calculation)
        const { data: allLogs, error: allError } = await supabase
          .from("child_logs")
          .select("time_spent_minutes, date, daily_goal_at_time")
          .eq("child_id", child.id)
          .order("date", { ascending: true });

        if (allError) continue;

        const totalSessions = allLogs?.length || 0;
        const totalTime =
          allLogs?.reduce((sum, log) => sum + log.time_spent_minutes, 0) || 0;
        const averageSessionLength =
          totalSessions > 0 ? totalTime / totalSessions : 0;

        // Calculate goal achievement rate over last 30 days (unchanged for now)
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        // Sum per-day goals for last 30 days (rest day => 0)
        let totalGoalTime = 0;
        if (goalsData) {
          for (let i = 0; i < 30; i++) {
            const d = new Date();
            d.setDate(d.getDate() - i);
            const dStr = getLocalDateString(d);
            const g = getGoalForDate(goalsData as any, dStr);
            totalGoalTime += g === null ? 0 : g;
          }
        } else {
          totalGoalTime = 0;
        }
        const logsLast30Days = (allLogs || []).filter(
          (l) => new Date(l.date) >= thirtyDaysAgo,
        );
        const totalTimeLast30 = logsLast30Days.reduce(
          (sum, l) => sum + (l.time_spent_minutes || 0),
          0,
        );
        const goalAchievementRate =
          totalGoalTime > 0 ? (totalTimeLast30 / totalGoalTime) * 100 : 0;

        // Calculate consistency this month = days with any logs this month / days elapsed this month
        const todayDate = new Date();
        const monthStartStr = `${todayDate.getFullYear()}-${String(
          todayDate.getMonth() + 1,
        ).padStart(2, "0")}-01`;
        const todayMonthStr = `${todayDate.getFullYear()}-${String(
          todayDate.getMonth() + 1,
        ).padStart(2, "0")}-${String(todayDate.getDate()).padStart(2, "0")}`;

        const uniqueLoggedDaysThisMonth = new Set(
          (allLogs || [])
            .filter(
              (l) =>
                (l.time_spent_minutes || 0) > 0 &&
                l.date >= monthStartStr &&
                l.date <= todayMonthStr,
            )
            .map((l) => l.date),
        );

        const daysElapsedThisMonth = todayDate.getDate(); // Day of month (1-31)
        const consistencyRate =
          daysElapsedThisMonth > 0
            ? (uniqueLoggedDaysThisMonth.size / daysElapsedThisMonth) * 100
            : 0;

        // Calculate streaks - aggregate by date first
        const dailyTotalsForStreak = new Map<string, number>();
        allLogs?.forEach((log) => {
          const current = dailyTotalsForStreak.get(log.date) || 0;
          dailyTotalsForStreak.set(log.date, current + log.time_spent_minutes);
        });

        // Calculate current streak (from today backwards) - minimum 30 minutes
        // Rest days (null goal) don't break the streak - they're treated as successful days
        const MINIMUM_STREAK_MINUTES = 30;
        let currentStreak = 0;
        let checkDate = new Date();

        while (true) {
          const checkDateStr = getLocalDateString(checkDate);
          const dayTotal = dailyTotalsForStreak.get(checkDateStr) || 0;

          // Check if this day was a rest day by looking at logs
          // If a log exists with daily_goal_at_time === null, it's a rest day
          const dayLogs =
            allLogs?.filter((log) => log.date === checkDateStr) || [];
          let isRestDay = dayLogs.some(
            (log) => log.daily_goal_at_time === null,
          );
          if (!isRestDay && goalsData) {
            const g = getGoalForDate(goalsData as any, checkDateStr);
            isRestDay = g === null;
          }

          // Rest days don't break the streak - continue counting
          // Days with activity >= minimum also continue the streak
          if (isRestDay || dayTotal >= MINIMUM_STREAK_MINUTES) {
            currentStreak++;
            checkDate.setDate(checkDate.getDate() - 1);
          } else {
            break;
          }

          // Prevent infinite loop
          if (currentStreak > 365) break;
        }

        // Calculate longest streak (any period) — rest days don't break it
        let longestStreak = currentStreak;
        if (allLogs && allLogs.length > 0) {
          const allDates = allLogs.map((l) => l.date).sort();
          const walkDate = new Date(allDates[0] + "T00:00:00");
          const todayWalk = new Date();
          todayWalk.setHours(0, 0, 0, 0);
          let tempStreak = 0;

          while (walkDate <= todayWalk) {
            const walkDateStr = getLocalDateString(walkDate);
            const dayTotal = dailyTotalsForStreak.get(walkDateStr) || 0;

            const dayLogs =
              allLogs?.filter((log) => log.date === walkDateStr) || [];
            let isRestDay = dayLogs.some(
              (log) => log.daily_goal_at_time === null,
            );
            if (!isRestDay && goalsData) {
              const g = getGoalForDate(goalsData as any, walkDateStr);
              isRestDay = g === null;
            }

            if (isRestDay || dayTotal >= MINIMUM_STREAK_MINUTES) {
              tempStreak++;
              longestStreak = Math.max(longestStreak, tempStreak);
            } else {
              tempStreak = 0;
            }

            walkDate.setDate(walkDate.getDate() + 1);
          }
        }

        progressArray.push({
          child_id: child.id,
          child_name: child.name,
          today_minutes: todayMinutes,
          today_goal: dailyGoal,
          weekly_total: weeklyTotal,
          weekly_goal: weeklyGoal,
          goal_achievement_rate: goalAchievementRate,
          consistency_rate: consistencyRate,
          total_sessions: totalSessions,
          average_session_length: averageSessionLength,
          longest_streak: longestStreak,
          current_streak: currentStreak,
        });
      }

      setChildProgress(progressArray);
      setTotalTodayMinutes(totalToday);
      setTotalWeeklyMinutes(totalWeekly);
    } catch (error) {
      console.error("Error fetching child progress:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSelfProgress = async () => {
    if (!user) return;

    try {
      // Get user's own goals
      const { data: goalsData } = await supabase
        .from("user_goals")
        .select(
          "weekly_goal_minutes, monday_goal_minutes, tuesday_goal_minutes, wednesday_goal_minutes, thursday_goal_minutes, friday_goal_minutes, saturday_goal_minutes, sunday_goal_minutes",
        )
        .eq("user_id", user.id)
        .single();

      const todayStr = getLocalDateString();
      const todayGoal = goalsData
        ? getGoalForDate(goalsData as any, todayStr)
        : null;
      const dailyGoal = todayGoal === null ? 0 : (todayGoal ?? 0);
      const weeklyGoal = goalsData
        ? getWeeklyGoalTotal(goalsData as any)
        : 0;

      // Get today's logs
      const today = todayStr;
      const { data: todayLogs } = await supabase
        .from("daily_logs")
        .select("time_spent_minutes")
        .eq("user_id", user.id)
        .eq("date", today);

      const todayMinutes =
        todayLogs?.reduce(
          (sum, log) => sum + (log.time_spent_minutes || 0),
          0,
        ) || 0;

      // Get weekly logs
      const weekStart = new Date();
      weekStart.setDate(weekStart.getDate() - weekStart.getDay());
      const weekStartStr = getLocalDateString(weekStart);
      const weekEndStr = todayStr;

      const { data: weeklyLogs } = await supabase
        .from("daily_logs")
        .select("time_spent_minutes, date")
        .eq("user_id", user.id)
        .gte("date", weekStartStr)
        .lte("date", weekEndStr);

      const weeklyTotal =
        weeklyLogs?.reduce(
          (sum, log) => sum + (log.time_spent_minutes || 0),
          0,
        ) || 0;

      // Calculate consistency and streaks
      const { data: allLogs } = await supabase
        .from("daily_logs")
        .select("date, time_spent_minutes, daily_goal_at_time")
        .eq("user_id", user.id)
        .order("date", { ascending: false });

      let currentStreak = 0;
      let longestStreak = 0;
      let totalSessions = 0;
      let totalMinutes = 0;

      if (allLogs) {
        // Aggregate logs by date first
        const dailyTotalsForStreak = new Map<string, number>();
        allLogs.forEach((log) => {
          const current = dailyTotalsForStreak.get(log.date) || 0;
          dailyTotalsForStreak.set(
            log.date,
            current + (log.time_spent_minutes || 0),
          );
          totalMinutes += log.time_spent_minutes || 0;
        });
        totalSessions = allLogs.length;

        // Calculate current streak (from today backwards) - minimum 30 minutes
        const MINIMUM_STREAK_MINUTES = 30;
        let checkDate = new Date();

        while (true) {
          const checkDateStr = getLocalDateString(checkDate);
          const dayTotal = dailyTotalsForStreak.get(checkDateStr) || 0;

          // Rest day detection for self: prefer historical log goal (NULL), else fall back to current goals
          const dayLogs = allLogs.filter((l) => l.date === checkDateStr);
          let isRestDay = dayLogs.some(
            (l: any) => l.daily_goal_at_time === null,
          );
          if (!isRestDay && goalsData) {
            const g = getGoalForDate(goalsData as any, checkDateStr);
            isRestDay = g === null;
          }

          if (isRestDay || dayTotal >= MINIMUM_STREAK_MINUTES) {
            currentStreak++;
            checkDate.setDate(checkDate.getDate() - 1);
          } else {
            break;
          }

          // Prevent infinite loop
          if (currentStreak > 365) break;
        }

        // Calculate longest streak (any period) — rest days don't break it
        longestStreak = currentStreak;
        if (allLogs && allLogs.length > 0) {
          const allDates = allLogs.map((l: any) => l.date).sort();
          const walkDate = new Date(allDates[0] + "T00:00:00");
          const todayWalk = new Date();
          todayWalk.setHours(0, 0, 0, 0);
          let tempStreak = 0;

          while (walkDate <= todayWalk) {
            const walkDateStr = getLocalDateString(walkDate);
            const dayTotal = dailyTotalsForStreak.get(walkDateStr) || 0;

            const dayLogs = allLogs.filter((l: any) => l.date === walkDateStr);
            let isRestDay = dayLogs.some(
              (l: any) => l.daily_goal_at_time === null,
            );
            if (!isRestDay && goalsData) {
              const g = getGoalForDate(goalsData as any, walkDateStr);
              isRestDay = g === null;
            }

            if (isRestDay || dayTotal >= MINIMUM_STREAK_MINUTES) {
              tempStreak++;
              longestStreak = Math.max(longestStreak, tempStreak);
            } else {
              tempStreak = 0;
            }

            walkDate.setDate(walkDate.getDate() + 1);
          }
        }
      }

      const averageSessionLength =
        totalSessions > 0 ? totalMinutes / totalSessions : 0;
      // Consistency this month = days with any logs this month / days elapsed this month
      let consistencyRate = 0;
      if (allLogs && allLogs.length > 0) {
        const todayDate = new Date();
        const monthStartStr = `${todayDate.getFullYear()}-${String(
          todayDate.getMonth() + 1,
        ).padStart(2, "0")}-01`;
        const todayStr = `${todayDate.getFullYear()}-${String(
          todayDate.getMonth() + 1,
        ).padStart(2, "0")}-${String(todayDate.getDate()).padStart(2, "0")}`;

        const uniqueLoggedDaysThisMonth = new Set(
          allLogs
            .filter(
              (l) =>
                (l.time_spent_minutes || 0) > 0 &&
                l.date >= monthStartStr &&
                l.date <= todayStr,
            )
            .map((l) => l.date),
        );

        const daysElapsedThisMonth = todayDate.getDate(); // Day of month (1-31)
        consistencyRate =
          daysElapsedThisMonth > 0
            ? (uniqueLoggedDaysThisMonth.size / daysElapsedThisMonth) * 100
            : 0;
      }

      // Create a self-progress object similar to child progress
      const selfProgress: ChildProgress = {
        child_id: user.id,
        child_name: "Your Progress",
        today_minutes: todayMinutes,
        today_goal: dailyGoal,
        weekly_total: weeklyTotal,
        weekly_goal: weeklyGoal,
        goal_achievement_rate:
          dailyGoal === 0 ? 100 : (todayMinutes / dailyGoal) * 100,
        consistency_rate: consistencyRate,
        total_sessions: totalSessions,
        average_session_length: averageSessionLength,
        longest_streak: longestStreak,
        current_streak: currentStreak,
      };

      setChildProgress([selfProgress]);
      setTotalTodayMinutes(todayMinutes);
      setTotalWeeklyMinutes(weeklyTotal);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching self progress:", error);
      setLoading(false);
    }
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round((minutes % 60) * 10) / 10; // Round to 1 decimal
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading dashboard...</div>
      </div>
    );
  }

  if (children.length === 0 && treatmentType === "child") {
    return (
      <div className="text-center py-8">
        <h2
          className={`text-2xl font-bold mb-4 ${
            darkMode ? "text-white" : "text-brand-dark-blue"
          }`}
        >
          Welcome to Amblyfy
        </h2>
        <p
          className={`text-lg ${darkMode ? "text-gray-300" : "text-gray-600"}`}
        >
          Add children in Settings to start tracking their progress.
        </p>
      </div>
    );
  }

  // For self-treatment users, always show the dashboard and quick nav

  return (
    <div className="w-full space-y-8">
      {/* Header */}

      {/* Quick Navigation for Self and Child Treatment */}
      {(treatmentType === "self" || treatmentType === "child") && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <button
            onClick={() => onNavigate?.("log-progress")}
            className={`p-6 rounded-xl text-center transition-all duration-200 hover:scale-105 ${
              darkMode
                ? "bg-gradient-to-br from-brand-dark-blue to-brand-cyan text-white shadow-lg"
                : "bg-gradient-to-br from-blue-50 to-cyan-50 text-gray-800 border border-blue-200"
            }`}
          >
            <div className="text-3xl mb-3">📝</div>
            <h3 className="text-lg font-semibold mb-2">Log Activity</h3>
            <p className="text-sm opacity-80">
              {treatmentType === "child"
                ? "Record children's daily treatment sessions"
                : "Record your daily treatment session"}
            </p>
          </button>

          <button
            onClick={() => onNavigate?.("calendar")}
            className={`p-6 rounded-xl text-center transition-all duration-200 hover:scale-105 ${
              darkMode
                ? "bg-gradient-to-br from-brand-cyan to-brand-light-green text-white shadow-lg"
                : "bg-gradient-to-br from-cyan-50 to-green-50 text-gray-800 border border-cyan-200"
            }`}
          >
            <div className="text-3xl mb-3">📅</div>
            <h3 className="text-lg font-semibold mb-2">Calendar</h3>
            <p className="text-sm opacity-80">
              {treatmentType === "child"
                ? "View children's progress history"
                : "View your progress history"}
            </p>
          </button>

          <button
            onClick={() => onNavigate?.("statistics")}
            className={`p-6 rounded-xl text-center transition-all duration-200 hover:scale-105 ${
              darkMode
                ? "bg-gradient-to-br from-brand-dark-green to-brand-pale-green text-white shadow-lg"
                : "bg-gradient-to-br from-green-50 to-emerald-50 text-gray-800 border border-green-200"
            }`}
          >
            <div className="text-3xl mb-3">📊</div>
            <h3 className="text-lg font-semibold mb-2">Statistics</h3>
            <p className="text-sm opacity-80">
              {treatmentType === "child"
                ? "Detailed children's progress analysis"
                : "Detailed progress analysis"}
            </p>
          </button>
        </div>
      )}

      {/* Overall Summary - Show for both child and self treatment */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 w-full">
        <div
          className={`p-6 rounded-2xl ${
            darkMode ? "bg-gray-800" : "bg-white"
          } ${
            darkMode
              ? "shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
              : "shadow-lg"
          } text-center`}
        >
          <h3
            className={`text-lg font-semibold mb-4 ${
              darkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {treatmentType === "child" ? "Total Today" : "Today's Progress"}
          </h3>
          <div
            className={`text-3xl font-bold ${
              darkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {formatTime(totalTodayMinutes)}
          </div>
          <div
            className={`text-sm mt-2 ${
              darkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            {treatmentType === "child"
              ? "Across all children"
              : "Your daily total"}
          </div>
        </div>
        <div
          className={`p-6 rounded-2xl ${
            darkMode ? "bg-gray-800" : "bg-white"
          } ${
            darkMode
              ? "shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
              : "shadow-lg"
          } text-center`}
        >
          <h3
            className={`text-lg font-semibold mb-4 ${
              darkMode ? "text-white" : "text-gray-900"
            }`}
          >
            This Week
          </h3>
          <div
            className={`text-3xl font-bold ${
              darkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {formatTime(totalWeeklyMinutes)}
          </div>
          <div
            className={`text-sm mt-2 ${
              darkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Weekly total
          </div>
        </div>
        <div
          className={`p-6 rounded-2xl ${
            darkMode ? "bg-gray-800" : "bg-white"
          } ${
            darkMode
              ? "shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
              : "shadow-lg"
          } text-center`}
        >
          <h3
            className={`text-lg font-semibold mb-4 ${
              darkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Consistency
          </h3>
          <div
            className={`text-3xl font-bold ${
              darkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {childProgress.length === 0
              ? 0
              : Math.round(
                  childProgress.reduce(
                    (sum, child) => sum + child.consistency_rate,
                    0,
                  ) / childProgress.length,
                )}
            %
          </div>
          <div
            className={`text-sm mt-2 ${
              darkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            {treatmentType === "child"
              ? "Monthly consistency rate"
              : "Your monthly consistency rate"}
          </div>
        </div>
        <div
          className={`p-6 rounded-2xl ${
            darkMode ? "bg-gray-800" : "bg-white"
          } ${
            darkMode
              ? "shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
              : "shadow-lg"
          } text-center`}
        >
          <h3
            className={`text-lg font-semibold mb-4 ${
              darkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {treatmentType === "child" ? "Active Children" : "Sessions"}
          </h3>
          <div
            className={`text-3xl font-bold ${
              darkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {treatmentType === "child"
              ? children.length
              : childProgress[0]?.total_sessions || 0}
          </div>
          <div
            className={`text-sm mt-2 ${
              darkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            {treatmentType === "child"
              ? "Registered children"
              : "Total sessions"}
          </div>
        </div>
      </div>

      {/* Removed Best Performing Child section as requested */}

      {/* Self User Progress Rings - Only show for self treatment */}
      {treatmentType === "self" && childProgress[0] && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Weekly Progress Ring */}
          <div
            className={`p-6 rounded-2xl ${
              darkMode ? "bg-gray-800" : "bg-white"
            } ${
              darkMode
                ? "shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
                : "shadow-lg"
            } flex flex-col items-center justify-center`}
          >
            <h3
              className={`text-lg font-semibold mb-4 ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Weekly Progress
            </h3>
            <CircularProgress
              percentage={
                childProgress[0].weekly_goal > 0
                  ? (childProgress[0].weekly_total / childProgress[0].weekly_goal) * 100
                  : 100
              }
              size={120}
              strokeWidth={10}
              color="#10B981"
            >
              <div className="text-center">
                <div
                  className={`text-2xl font-bold ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {formatTime(childProgress[0].weekly_total)}
                </div>
                <div
                  className={`text-xs mt-1 ${
                    darkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {childProgress[0].weekly_goal > 0
                    ? `of ${formatTime(childProgress[0].weekly_goal)}`
                    : "All rest days"}
                </div>
              </div>
            </CircularProgress>
            <div
              className={`text-sm mt-4 ${
                darkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              {childProgress[0].weekly_goal > 0
                ? Math.round(
                    (childProgress[0].weekly_total / childProgress[0].weekly_goal) * 100,
                  )
                : 100}
              %
            </div>
          </div>

          {/* Today's Progress Ring */}
          <div
            className={`p-6 rounded-2xl ${
              darkMode ? "bg-gray-800" : "bg-white"
            } ${
              darkMode
                ? "shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
                : "shadow-lg"
            } flex flex-col items-center justify-center`}
          >
            <h3
              className={`text-lg font-semibold mb-4 ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Today's Progress
            </h3>
            <CircularProgress
              percentage={
                childProgress[0].today_goal === 0
                  ? 100
                  : (childProgress[0].today_minutes / childProgress[0].today_goal) *
                    100
              }
              size={120}
              strokeWidth={10}
              color="#3B82F6"
            >
              <div className="text-center">
                <div
                  className={`text-2xl font-bold ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {formatTime(childProgress[0].today_minutes)}
                </div>
                <div
                  className={`text-xs mt-1 ${
                    darkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {childProgress[0].today_goal === 0
                    ? "Rest day"
                    : `of ${formatTime(childProgress[0].today_goal)}`}
                </div>
              </div>
            </CircularProgress>
            <div
              className={`text-sm mt-4 ${
                darkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              {childProgress[0].today_goal === 0
                ? 100
                : Math.round(
                    (childProgress[0].today_minutes /
                      childProgress[0].today_goal) *
                      100,
                  )}
              %
            </div>
          </div>

          {/* Current Streak - No Ring */}
          <div
            className={`p-6 rounded-2xl ${
              darkMode ? "bg-gray-800" : "bg-white"
            } ${
              darkMode
                ? "shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
                : "shadow-lg"
            } flex flex-col items-center justify-center text-center`}
          >
            <h3
              className={`text-lg font-semibold mb-4 ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Current Streak
            </h3>
            <div
              className={`text-5xl font-bold ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              {childProgress[0].current_streak}
            </div>
            <div
              className={`text-sm mt-2 ${
                darkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              days
            </div>
            <div
              className={`text-sm mt-3 ${
                darkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              Keep it going! 🔥
            </div>
          </div>

          {/* Average Session - No Ring */}
          <div
            className={`p-6 rounded-2xl ${
              darkMode ? "bg-gray-800" : "bg-white"
            } ${
              darkMode
                ? "shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
                : "shadow-lg"
            } flex flex-col items-center justify-center text-center`}
          >
            <h3
              className={`text-lg font-semibold mb-4 ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Avg. Session
            </h3>
            <div
              className={`text-4xl font-bold ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              {formatTime(childProgress[0].average_session_length)}
            </div>
            <div
              className={`text-sm mt-2 ${
                darkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              per session
            </div>
            <div
              className={`text-sm mt-3 ${
                darkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              {childProgress[0].total_sessions} sessions
            </div>
          </div>
        </div>
      )}

      {/* Individual Child Progress - Only show for child treatment */}
      {treatmentType === "child" && (
        <div
          className={`grid gap-6 ${
            children.length <= 3
              ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
              : "grid-cols-1 lg:grid-cols-2"
          }`}
        >
          {childProgress.map((child) => (
            <div
              key={child.child_id}
              className={`p-6 rounded-2xl ${
                darkMode ? "bg-gray-800" : "bg-white"
              } ${
                darkMode
                  ? "shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
                  : "shadow-lg"
              }`}
            >
              <h3
                className={`text-xl font-semibold mb-4 ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {child.child_name}
              </h3>

              <div className="space-y-4">
                {/* Today's Progress */}
                <div className="flex items-center justify-between">
                  <div>
                    <div
                      className={`text-sm ${
                        darkMode ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      Today's Progress
                    </div>
                    <div
                      className={`text-lg font-bold ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {child.today_goal === 0
                        ? "Rest day"
                        : `${formatTime(child.today_minutes)} / ${formatTime(child.today_goal)}`}
                    </div>
                  </div>
                  <CircularProgress
                    percentage={child.today_goal === 0 ? 100 : (child.today_minutes / child.today_goal) * 100}
                    size={60}
                    strokeWidth={6}
                    color={
                      child.today_goal === 0 || (child.today_minutes / child.today_goal) * 100 >= 100
                        ? "#22c55e"
                        : (child.today_minutes / child.today_goal) * 100 >= 75
                          ? "#10b981"
                          : (child.today_minutes / child.today_goal) * 100 >= 50
                            ? "#eab308"
                            : "#f97316"
                    }
                    backgroundColor={darkMode ? "#374151" : "#e5e7eb"}
                  >
                    <div
                      className={`text-xs font-semibold ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {child.today_goal === 0
                        ? 100
                        : Math.round(
                            (child.today_minutes / child.today_goal) * 100,
                          )}
                      %
                    </div>
                  </CircularProgress>
                </div>

                {/* Weekly Progress */}
                <div className="flex items-center justify-between">
                  <div>
                    <div
                      className={`text-sm ${
                        darkMode ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      This Week
                    </div>
                    <div
                      className={`text-lg font-bold ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {formatTime(child.weekly_total)} /{" "}
                      {formatTime(child.weekly_goal)}
                    </div>
                  </div>
                  <CircularProgress
                    percentage={(child.weekly_total / child.weekly_goal) * 100}
                    size={60}
                    strokeWidth={6}
                    color={
                      (child.weekly_total / child.weekly_goal) * 100 >= 100
                        ? "#2563eb"
                        : (child.weekly_total / child.weekly_goal) * 100 >= 75
                          ? "#3b82f6"
                          : (child.weekly_total / child.weekly_goal) * 100 >= 50
                            ? "#eab308"
                            : "#f97316"
                    }
                    backgroundColor={darkMode ? "#374151" : "#e5e7eb"}
                  >
                    <div
                      className={`text-xs font-semibold ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {Math.round(
                        (child.weekly_total / child.weekly_goal) * 100,
                      )}
                      %
                    </div>
                  </CircularProgress>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div>
                    <div
                      className={`text-xs ${
                        darkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Sessions
                    </div>
                    <div
                      className={`text-sm font-semibold ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {child.total_sessions}
                    </div>
                  </div>
                  <div>
                    <div
                      className={`text-xs ${
                        darkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Avg Session
                    </div>
                    <div
                      className={`text-sm font-semibold ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {formatTime(child.average_session_length)}
                    </div>
                  </div>
                  <div>
                    <div
                      className={`text-xs ${
                        darkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Longest Streak
                    </div>
                    <div
                      className={`text-sm font-semibold ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {child.longest_streak} days
                    </div>
                  </div>
                  <div>
                    <div
                      className={`text-xs ${
                        darkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Consistency
                    </div>
                    <div
                      className={`text-sm font-semibold ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {Math.round(child.consistency_rate)}%
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Self Progress Card removed - now using rings above instead */}
    </div>
  );
};

export default ProgressDashboard;
